import { useContext, useMemo, useState } from 'react';
import { Box, Button, Card, Icon, Paragraph, Toast, useToast } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconCheckmarkFilled, IconHideFilled } from '@zillow/constellation-icons';
import { TourActionButton, TourActionType, TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';
import getTourDateTime from '../../utils/getTourDateTime';
import getTourCalendarUrl from '../../utils/getTourCalendarUrl';
import StringConstants from '../../constants/StringConstants';
import useUpdateStatusLabelCache from '../../hooks/useUpdateStatusLabelCache';
import usePageIdentifier from '../../hooks/usePageIdentifier';
import { AppContext } from '../../contexts/AppContextProvider';
import type { TourActionTriggerSource } from '../../constants/clickstreamEvents/tourScheduling';
import {
  emitInboxTourActionEvent,
  emitLeadMgmtTourActionEvent,
  INBOX_TOUR_ACTION_EVENT_TYPE_ID,
  LEAD_MGMT_TOUR_ACTION_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/tourScheduling';
import { PAGE_NAME } from '../../constants/pageName';
import TourCancellationModal from '../Modals/TourCancellationModal';
import TourStatusCardSkeleton from './Skeleton';
import { useAuth } from '../../AuthProvider';
import {
  RescheduleTourModal,
  RescheduleTourModalTriggers,
  usePerformTourAction,
  useTourForSatelliteConversationId,
} from '@zg-rentals/touring';
import { cardStyles, defaultViewActionStyles, stackedViewActionStyles } from './styles';

export type TourActionUI = {
  id: TourActionButton;
  text: string;
  icon: JSX.Element;
  onClick: () => void;
  action: TourActionTriggerSource;
};

type TourStatusCardProps = {
  conversationId: string;
  leadId?: string | null;
  listingAlias: string;
  // Drilled to TourCancellationModal (inbox: getMessages cache; lead management: lead row).
  renterName: string;
  showStackedView?: boolean;
  showViewInCalendarLink?: boolean;
};

export default function TourStatusCard({
  conversationId,
  leadId,
  listingAlias,
  renterName,
  showStackedView,
  showViewInCalendarLink,
}: TourStatusCardProps) {
  const { isImpersonator } = useContext(AppContext);
  const { enqueueToast } = useToast();
  const { user } = useAuth();
  const currentPage = usePageIdentifier();
  const updateStatusLabelCache = useUpdateStatusLabelCache({
    conversationId,
    listingAlias,
  });
  const [showTourCancellationModal, setShowTourCancellationModal] = useState(false);
  const [showTourRescheduleModal, setShowTourRescheduleModal] = useState(false);
  const [tourActionForModal, setTourActionForModal] = useState<
    TourActionType.IRENE_CANCEL | TourActionType.IRENE_DECLINE
  >(TourActionType.IRENE_CANCEL);

  const { mutate: performTourAction } = usePerformTourAction({
    errorDetails: 'TourStatusCard - Error fetching perform tour action',
  });

  const {
    data: tourData,
    isLoading,
    refetch,
  } = useTourForSatelliteConversationId(
    {
      conversationId,
      listingAlias,
    },
    {
      errorDetails: 'TourStatusCard - Error fetching tour for conversationId',
    },
  );

  const { allowedLandlordActions, id, startMs, zoneId } = tourData?.tour || {};
  const { tourDateTimeCapitalized, tourDateTimeLowerCase } = useMemo(() => {
    if (!startMs || !zoneId) {
      return { tourDateTimeCapitalized: '', tourDateTimeLowerCase: '' };
    }

    const tourStartMs = startMs.toString();

    return {
      tourDateTimeCapitalized: getTourDateTime({ startMs: tourStartMs, timeZone: zoneId, capitalizeText: true }),
      tourDateTimeLowerCase: getTourDateTime({ startMs: tourStartMs, timeZone: zoneId, capitalizeText: false }),
    };
  }, [startMs, zoneId]);

  const fireClickstreamEvent = (action: TourActionTriggerSource) => {
    if (currentPage === PAGE_NAME.leadManagement) {
      emitLeadMgmtTourActionEvent({ user, listing: { listingAlias }, triggerSource: action, lead: { leadId } });
    } else if (currentPage === PAGE_NAME.inbox) {
      emitInboxTourActionEvent(user, action, { listingAlias });
    }
  };

  const handleConfirmTour = () => {
    fireClickstreamEvent('confirm_tour');

    performTourAction(
      {
        postData: {
          actionType: TourActionType.IRENE_CONFIRM,
          listingAlias,
          tourId: id!,
        },
      },
      {
        onSuccess: (response) => {
          const message = `Tour for ${tourDateTimeLowerCase || 'the scheduled time'} confirmed`;
          enqueueToast(<Toast tone="success">{message}</Toast>);
          const startMs = response?.tour?.startMs;
          if (startMs) {
            updateStatusLabelCache(TourActionType.IRENE_CONFIRM, startMs);
          }
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">Tour couldn’t be confirmed. Wait and try again.</Toast>);
        },
      },
    );
  };

  const handleRescheduleSuccess = (startMs?: number) => {
    if (startMs) {
      updateStatusLabelCache(TourActionType.IRENE_CONFIRM, startMs);
      refetch();
    }
  };

  const handleRescheduleClick = () => {
    fireClickstreamEvent('reschedule_tour');
    setShowTourRescheduleModal(true);
  };

  const handleCancelClick = () => {
    fireClickstreamEvent('cancel_tour');
    setShowTourCancellationModal(true);
    setTourActionForModal(TourActionType.IRENE_CANCEL);
  };

  const handleDeclineClick = () => {
    fireClickstreamEvent('decline_tour');
    setShowTourCancellationModal(true);
    setTourActionForModal(TourActionType.IRENE_DECLINE);
  };

  const tourActionsUI: { [key in TourActionButton]: TourActionUI } = {
    [TourActionButton.CONFIRM]: {
      id: TourActionButton.CONFIRM,
      text: 'Confirm',
      icon: <IconCheckmarkFilled />,
      onClick: handleConfirmTour,
      action: 'confirm_tour',
    },
    [TourActionButton.IRENE_RESCHEDULE]: {
      id: TourActionButton.IRENE_RESCHEDULE,
      text: 'Reschedule',
      icon: <IconHideFilled />,
      onClick: handleRescheduleClick,
      action: 'reschedule_tour',
    },
    [TourActionButton.DECLINE]: {
      id: TourActionButton.DECLINE,
      text: 'Decline',
      icon: <IconHideFilled />,
      onClick: handleDeclineClick,
      action: 'decline_tour',
    },
    [TourActionButton.CANCEL]: {
      id: TourActionButton.CANCEL,
      text: 'Cancel',
      icon: <IconHideFilled />,
      onClick: handleCancelClick,
      action: 'cancel_tour',
    },
  };

  const orderedActions = useMemo(() => {
    // Get the keys of tourActionsUI in the order they were defined
    return Object.keys(tourActionsUI) as Array<TourActionButton>;
  }, [tourActionsUI]);

  const actions = useMemo(() => {
    if (!allowedLandlordActions || allowedLandlordActions.length === 0) {
      return [];
    }

    return orderedActions
      .filter((action) => allowedLandlordActions.includes(action))
      .map((action) => tourActionsUI[action]);
  }, [allowedLandlordActions, orderedActions]);

  if (isLoading) return <TourStatusCardSkeleton showStackedView={showStackedView} />;
  if (!tourData?.tour) return null;
  const { id: tourId, tourStatus, title } = tourData.tour;
  const isTourConfirmed = tourStatus === TourStatusForZcheduler.CONFIRMED;
  const borderColor = isTourConfirmed ? 'blue.400' : 'gray.300';
  // The calendar never loads declined tours and hides cancelled ones by default, so the
  // deep link would dead-end in a "tour not found" toast for those statuses.
  const shouldShowViewInCalendarLink =
    !!showViewInCalendarLink &&
    tourStatus !== TourStatusForZcheduler.DECLINED &&
    tourStatus !== TourStatusForZcheduler.CANCELLED;

  const renderActions = () => {
    const eventTypeId =
      currentPage === PAGE_NAME.leadManagement ? LEAD_MGMT_TOUR_ACTION_EVENT_TYPE_ID : INBOX_TOUR_ACTION_EVENT_TYPE_ID;

    return (
      <Box css={showStackedView ? stackedViewActionStyles : defaultViewActionStyles}>
        {shouldShowViewInCalendarLink && (
          <Button
            asChild
            data-eventid={eventTypeId}
            size="sm"
            tone="neutral"
            emphasis="outlined"
            css={{
              gridColumn: showStackedView ? '1 / -1' : 'auto',
            }}
          >
            <a
              href={getTourCalendarUrl({ listingAlias, tourId })}
              onClick={() => fireClickstreamEvent('view_in_calendar')}
            >
              {StringConstants.VIEW_IN_CALENDAR}
            </a>
          </Button>
        )}
        {actions.map(({ id, text, onClick }) => {
          const isConfirmAction = id === TourActionButton.CONFIRM;

          return (
            <Button
              data-eventid={eventTypeId}
              key={text}
              size="sm"
              onClick={onClick}
              disabled={isImpersonator}
              tone={isConfirmAction ? 'brand' : 'neutral'}
              emphasis={isConfirmAction ? 'filled' : 'outlined'}
              css={{
                gridColumn: showStackedView && isConfirmAction ? '1 / -1' : 'auto',
              }}
            >
              {text}
            </Button>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      {showTourCancellationModal && (
        <TourCancellationModal
          action={tourActionForModal}
          conversationId={conversationId}
          leadId={leadId}
          listingAlias={listingAlias}
          onClose={() => setShowTourCancellationModal(false)}
          renterName={renterName}
          shouldShowCancellationReasons={false}
          tourDateTime={tourDateTimeLowerCase}
          tourId={tourId}
        />
      )}

      {showTourRescheduleModal && (
        <RescheduleTourModal
          clickStreamUser={user}
          listingAlias={listingAlias}
          tourId={tourData?.tour?.id}
          tourTitle={tourData?.tour?.title}
          triggerSource={
            currentPage === PAGE_NAME.leadManagement
              ? RescheduleTourModalTriggers.LEAD_MANAGEMENT
              : RescheduleTourModalTriggers.SATELLITE
          }
          onClose={() => setShowTourRescheduleModal(false)}
          onRescheduleSuccess={handleRescheduleSuccess}
        />
      )}

      <Card
        elevated={false}
        css={{
          borderLeftColor: borderColor,
          ...cardStyles,
          ...(!showStackedView && {
            md: {
              flexDirection: 'row',
            },
          }),
        }}
      >
        {isTourConfirmed ? (
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: { base: '5% auto', md: '8% auto' },
              alignItems: 'center',
              gap: 'tighter',
            }}
          >
            <Icon size="sm" css={{ color: 'green' }}>
              <IconCheckmarkCircleFilled />
            </Icon>
            <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
            <Paragraph css={{ gridColumn: '2' }} textStyle="body-sm">
              {tourDateTimeCapitalized || 'Tour time unavailable'}
            </Paragraph>
          </Box>
        ) : (
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
            <Paragraph textStyle="body-sm">{tourDateTimeCapitalized || 'Tour time unavailable'}</Paragraph>
          </Box>
        )}

        {(!!actions.length || shouldShowViewInCalendarLink) && renderActions()}
      </Card>
    </>
  );
}
